export {};

declare global {
  interface Window {
    Square?: {
      payments: (
        applicationId: string,
        locationId: string
      ) => Promise<SquarePayments>;
    };
  }
}

export interface CardOptions {
  style?: Record<string, Record<string, string>>;
  includeInputLabels?: boolean;
}

export interface SquarePayments {
  card: (options?: CardOptions) => Promise<SquareCard>;
}

/** Subset of Square's `BillingContact` — see
 * https://developer.squareup.com/reference/sdks/web/payments/objects/ChargeCardVerificationDetails */
export interface BillingContact {
  postalCode?: string;
  addressLines?: string[];
  city?: string;
  state?: string;
  countryCode?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  phone?: string;
}

export interface TokenizeVerificationDetails {
  amount: string;
  currencyCode: string;
  intent: 'CHARGE' | 'CHARGE_AND_STORE';
  billingContact?: BillingContact;
  customerInitiated?: boolean;
  sellerKeyedIn?: boolean;
}

export interface SquareCard {
  attach: (selector: string) => Promise<void>;
  destroy: () => Promise<void>;
  tokenize: (verificationDetails?: TokenizeVerificationDetails) => Promise<SquareTokenizeResult>;
}

export interface SquareTokenizeResult {
  status: 'OK' | 'INVALID' | 'ERROR';
  token?: string;
  errors?: Array<{ message?: string }>;
}

export function loadSquareSdk(sdkUrl: string): Promise<void> {
  if (window.Square) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(
    `script[data-square-sdk="${sdkUrl}"]`
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Square SDK')), {
        once: true,
      });
      if (window.Square) resolve();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.dataset.squareSdk = sdkUrl;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Square SDK'));
    document.head.appendChild(script);
  });
}
